<template>
  <div class="w-full">
    <div v-if="!points.length" class="text-sm text-gray-400 py-6 text-center">
      {{ $t('graphiqueBatterie.aucunHistorique') }}
    </div>
    <div v-else class="overflow-x-auto">
      <svg :viewBox="`0 0 ${W} ${H}`" class="w-full" :style="{minWidth: minWidth + 'px'}"
           preserveAspectRatio="xMidYMid meet" role="img" :aria-label="$t('graphiqueBatterie.ariaQuotidien')">
        <!-- Seuils -->
        <g v-for="t in thresholdLines" :key="t.label">
          <line :x1="padL" :x2="W - padR" :y1="t.y" :y2="t.y"
                :stroke="t.color" stroke-width="1" stroke-dasharray="4 3" opacity="0.7" />
          <text :x="W - padR" :y="t.y - 3" text-anchor="end" :fill="t.color"
                font-size="10">{{ t.label }}</text>
        </g>

        <!-- Bandes min→max quotidiennes -->
        <g v-for="(p, i) in points" :key="'bar' + i">
          <rect :x="p.x - barW / 2" :y="p.yMax" :width="barW"
                :height="Math.max(1, p.yMin - p.yMax)" :rx="Math.min(2, barW / 2)"
                :fill="p.color" opacity="0.55">
            <title>{{ $t('graphiqueBatterie.infoJour', { jour: p.day, min: p.min, max: p.max, rebond: p.rebound, source: srcLabel(p.src), n: p.n }) }}</title>
          </rect>
        </g>

        <!-- Ligne des maxima (tendance) -->
        <polyline :points="maxLine" fill="none" stroke="#374151" stroke-width="1.5"
                  stroke-linejoin="round" stroke-linecap="round" />
        <g v-for="(p, i) in points" :key="'dot' + i">
          <circle :cx="p.x" :cy="p.yMax" r="2" fill="#374151" />
        </g>

        <!-- Axe Y (ticks mV) -->
        <g v-for="tick in yTicks" :key="'y' + tick">
          <text :x="padL - 6" :y="yOf(tick) + 3" text-anchor="end" fill="#9ca3af" font-size="10">{{ tick }}</text>
        </g>

        <!-- Axe X (quelques dates) -->
        <g v-for="lbl in xLabels" :key="'x' + lbl.i">
          <text :x="lbl.x" :y="H - 6" text-anchor="middle" fill="#9ca3af" font-size="10">{{ lbl.text }}</text>
        </g>
      </svg>

      <!-- Capacité restante par jour (cap_last) — MÊME axe X, échelle propre.
           Sur plusieurs semaines c'est la seule courbe où une dérive de consommation se lit
           directement : une droite qui s'incline, là où les mV plafonnent en float. -->
      <svg v-if="capPoints.length > 1" :viewBox="`0 0 ${W} ${CH}`" class="w-full"
           :style="{minWidth: minWidth + 'px'}" preserveAspectRatio="xMidYMid meet"
           role="img" :aria-label="$t('graphiqueBatterie.ariaCapacite')">
        <line :x1="padL" :x2="W - padR" :y1="CH - capPadB" :y2="CH - capPadB"
              stroke="#e5e7eb" stroke-width="1" />
        <!-- Amplitude du jour (min→max) en fond, tendance (dernière mesure) en ligne -->
        <g v-for="p in capPoints" :key="'capbar' + p.i">
          <rect v-if="p.yMin != null && p.yMax != null" :x="p.x - capBarW / 2" :y="p.yMax"
                :width="capBarW" :height="Math.max(1, p.yMin - p.yMax)" fill="#0d9488" opacity="0.25" />
        </g>
        <polyline :points="capLine" fill="none" stroke="#0d9488" stroke-width="1.6"
                  stroke-linejoin="round" stroke-linecap="round" />
        <g v-for="p in capPoints" :key="'capdot' + p.i">
          <circle :cx="p.x" :cy="p.y" r="2" fill="#0d9488">
            <title>{{ p.min != null ? $t('graphiqueBatterie.capFinJourneeMinMax', { jour: p.day, cap: p.last, min: p.min, max: p.max }) : $t('graphiqueBatterie.capFinJournee', { jour: p.day, cap: p.last }) }}</title>
          </circle>
        </g>
        <g v-for="tick in capTicks" :key="'capy' + tick.v">
          <text :x="padL - 6" :y="tick.y + 3" text-anchor="end" fill="#9ca3af" font-size="10">{{ tick.v }}</text>
        </g>
        <text :x="W - padR" :y="capPadT + 8" text-anchor="end" fill="#0d9488" font-size="10">mA·h</text>
      </svg>

      <!-- Légende régime -->
      <div class="flex flex-wrap gap-3 mt-1 text-xs text-gray-500">
        <span class="inline-flex items-center gap-1"><span class="w-3 h-2 rounded-sm inline-block" style="background:#f59e0b"></span>{{ $t('graphiqueBatterie.solaire') }}</span>
        <span class="inline-flex items-center gap-1"><span class="w-3 h-2 rounded-sm inline-block" style="background:#3b82f6"></span>{{ $t('graphiqueBatterie.secteur') }}</span>
        <span class="inline-flex items-center gap-1"><span class="w-3 h-2 rounded-sm inline-block" style="background:#8b5cf6"></span>{{ $t('graphiqueBatterie.batterie') }}</span>
        <span class="inline-flex items-center gap-1"><span class="w-4 border-t-2 inline-block" style="border-color:#374151"></span>{{ $t('graphiqueBatterie.maxQuotidien') }}</span>
        <span v-if="capPoints.length > 1" class="inline-flex items-center gap-1"><span class="w-4 border-t-2 inline-block" style="border-color:#0d9488"></span>{{ $t('graphiqueBatterie.capaciteRestante') }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

export default {
  name: 'BatteryChart',
  props: {
    daily: { type: Array, default: () => [] },
    thresholds: { type: Object, default: () => ({ eco: 3400, eco_exit: 3600, shutdown: 2750, full: 4100 }) }
  },
  setup(props) {
    const { t } = useI18n();
    // Géométrie
    const W = 720, H = 240, padL = 44, padR = 12, padT = 12, padB = 24;
    const yLo = 2700, yHi = 4250; // couvre arrêt (2750) → pleine (4200)
    const plotH = H - padT - padB;
    const plotW = W - padL - padR;

    const yOf = (mv) => padT + (yHi - Math.max(yLo, Math.min(yHi, mv))) / (yHi - yLo) * plotH;

    const srcColor = (src) => ({
      solar: '#f59e0b', mains: '#3b82f6', battery: '#8b5cf6'
    }[src] || '#9ca3af');
    const srcLabel = (src) => ({ solar: t('graphiqueBatterie.solaire'), mains: t('graphiqueBatterie.secteur'), battery: t('graphiqueBatterie.batterie') }[src] || t('graphiqueBatterie.inconnu'));

    const points = computed(() => {
      const d = props.daily || [];
      const n = d.length;
      if (!n) return [];
      const step = plotW / n;
      const barW = Math.max(2, Math.min(22, step * 0.6));
      return d.map((row, i) => ({
        i, day: row.day,
        min: row.min_mv, max: row.max_mv, rebound: row.rebound, n: row.n, src: row.src,
        x: padL + step * (i + 0.5),
        yMin: yOf(row.min_mv),
        yMax: yOf(row.max_mv),
        barW,
        color: srcColor(row.src)
      }));
    });

    const minWidth = computed(() => Math.max(320, (props.daily || []).length * 10));

    // --- Sous-graphe capacité restante (mA·h) ---
    // Échelle Y calée sur les données, avec une amplitude plancher : sur une carte alimentée cap
    // ne bouge que du quantum de la jauge (±1 mA·h), et une auto-échelle nue transformerait ce
    // bruit en relief spectaculaire.
    const CH = 110, capPadT = 10, capPadB = 16;
    const capPlotH = CH - capPadT - capPadB;
    const CAP_MIN_SPAN = 50;

    const capRange = computed(() => {
      const rows = (props.daily || []).filter(r => r.cap_last != null && r.cap_last > 0);
      if (rows.length < 2) return null;
      let lo = Infinity, hi = -Infinity;
      for (const r of rows) {
        for (const v of [r.cap_last, r.cap_min, r.cap_max]) {
          if (v == null || v <= 0) continue;
          if (v < lo) lo = v;
          if (v > hi) hi = v;
        }
      }
      const mid = (lo + hi) / 2;
      const span = Math.max(hi - lo, CAP_MIN_SPAN) * 1.15;
      return { lo: Math.round(mid - span / 2), hi: Math.round(mid + span / 2) };
    });
    const capYOf = (v) => {
      const r = capRange.value;
      if (!r || v == null) return null;
      const c = Math.max(r.lo, Math.min(r.hi, v));
      return capPadT + (r.hi - c) / (r.hi - r.lo) * capPlotH;
    };
    const capPoints = computed(() => {
      if (!capRange.value) return [];
      // Même axe X que le graphe mV : on réutilise la position calculée pour le jour.
      return points.value
        .map((p, i) => {
          const row = (props.daily || [])[i];
          if (!row || row.cap_last == null || row.cap_last <= 0) return null;
          return { i, x: p.x, day: row.day, last: row.cap_last, min: row.cap_min, max: row.cap_max,
                   y: capYOf(row.cap_last), yMin: capYOf(row.cap_min), yMax: capYOf(row.cap_max) };
        })
        .filter(Boolean);
    });
    const capBarW = computed(() => (points.value.length ? points.value[0].barW : 4));
    const capLine = computed(() => capPoints.value.map(p => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' '));
    const capTicks = computed(() => {
      const r = capRange.value;
      if (!r) return [];
      return [r.hi, Math.round((r.lo + r.hi) / 2), r.lo].map(v => ({ v, y: capYOf(v) }));
    });

    const maxLine = computed(() => points.value.map(p => `${p.x.toFixed(1)},${p.yMax.toFixed(1)}`).join(' '));

    const tr = t; // `t` désigne les seuils dans le calcul ci-dessous
    const thresholdLines = computed(() => {
      const t = props.thresholds || {};
      const out = [];
      if (t.full)     out.push({ y: yOf(t.full),     color: '#16a34a', label: tr('graphiqueBatterie.seuilPleine', { mv: t.full }) });
      if (t.eco)      out.push({ y: yOf(t.eco),      color: '#d97706', label: tr('graphiqueBatterie.seuilEco', { mv: t.eco }) });
      if (t.shutdown) out.push({ y: yOf(t.shutdown), color: '#dc2626', label: tr('graphiqueBatterie.seuilArret', { mv: t.shutdown }) });
      return out;
    });

    const yTicks = [2750, 3000, 3400, 3800, 4200];

    const xLabels = computed(() => {
      const p = points.value;
      if (!p.length) return [];
      const idxs = p.length <= 6
        ? p.map((_, i) => i)
        : [0, Math.floor(p.length / 2), p.length - 1];
      return idxs.map(i => {
        const parts = p[i].day.split('-'); // YYYY-MM-DD
        return { i, x: p[i].x, text: `${parts[2]}/${parts[1]}` };
      });
    });

    return { W, H, padL, padR, points, minWidth, maxLine, thresholdLines, yTicks, xLabels, yOf, srcLabel,
             CH, capPadT, capPadB, capPoints, capLine, capTicks, capBarW };
  }
};
</script>
